import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { InlineRename } from '../ui/InlineRename';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { field } from '../create/Field';
import { IconInventory, IconTrash, IconPlus } from '../icons';

const KIND_LABEL: Record<string, string> = {
  warehouse: 'Warehouse',
  vehicle: 'Van',
  bin: 'Bin',
};

/**
 * Where stock can sit (PRD v2 §6.1). warehouse / van / bin are labels, not rules — the
 * tree decides what's inside what, so a shop can nest a bin under a van without the model
 * having an opinion about it.
 */
export function LocationsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const locations = useQuery(trpc.inventory.locations.queryOptions());
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'warehouse' | 'vehicle' | 'bin'>('bin');
  const [parentId, setParentId] = useState('');

  const refresh = () => {
    void qc.invalidateQueries();
    setAdding(false);
    setName('');
    setParentId('');
  };
  const setUp = useMutation(
    trpc.inventory.setUpLocations.mutationOptions({ onSuccess: refresh }),
  );
  const create = useMutation(
    trpc.inventory.locationCreate.mutationOptions({ onSuccess: refresh }),
  );
  const rename = useMutation(
    trpc.inventory.locationUpdate.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  const archive = useMutation(
    trpc.inventory.locationArchive.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );
  // `rename.error` was missing from this chain, so a refused rename was the one write on
  // the card that failed in silence — the name simply stayed as it was.
  const error = setUp.error ?? create.error ?? rename.error ?? archive.error;
  const state = shownState(locations, locations.data);

  return (
    <SectionCard
      id={id}
      title={counted('Stock locations', state, locations.data?.length ?? 0)}
    >
      {!locations.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your locations"
          icon={IconInventory}
          error={locations.error}
          onRetry={() => void locations.refetch()}
        />
      ) : (
        <>
          <StaleNotice
            show={refreshFailed(locations, locations.data)}
            onRetry={() => void locations.refetch()}
          />
          {error ? <Notice tone="danger">{error.message}</Notice> : null}

          {locations.data.length === 0 ? (
            <div className="mb-3">
              <p className="mb-2 text-sm text-muted-foreground">
                None yet. Most shops want a warehouse and one van per tech — that's one
                press.
              </p>
              <Button
                onClick={() => setUp.mutate({ ...idem() })}
                disabled={setUp.isPending}
              >
                {setUp.isPending ? 'Setting up…' : 'Set up my locations'}
              </Button>
            </div>
          ) : (
            <ul className="mb-3 divide-y divide-border text-sm">
              {locations.data.map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-2">
                  <div
                    className="min-w-0 flex-1"
                    style={{ paddingLeft: `${l.depth * 16}px` }}
                  >
                    <p className="truncate font-medium">{l.name}</p>
                    {/* Only when the shop has renamed the van to something that doesn't
                        already say whose it is. */}
                    {l.userName && !l.name.includes(l.userName) ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {l.userName}'s
                      </p>
                    ) : null}
                  </div>
                  <StatusBadge tone={l.kind === 'vehicle' ? 'brand' : 'neutral'}>
                    {KIND_LABEL[l.kind] ?? l.kind}
                  </StatusBadge>
                  {/* "Van 2" becomes "Danny's van" the week Danny starts. Renaming beats
                      archiving and re-creating, which would orphan the stock on it. */}
                  <InlineRename
                    value={l.name}
                    busy={rename.isPending}
                    onSave={(name) =>
                      rename.mutate({ locationId: l.id, name, ...idem() })
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Archive ${l.name}`}
                    disabled={archive.isPending}
                    onClick={() => archive.mutate({ locationId: l.id, ...idem() })}
                    className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)]"
                  >
                    <IconTrash size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {adding ? (
            <div className="flex flex-wrap items-end gap-2">
              <input
                className={`${field} min-w-[180px] flex-1`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name — e.g. Shelf A"
                aria-label="Location name"
                autoFocus
              />
              <select
                className={`${field} w-28`}
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
                aria-label="Location kind"
              >
                <option value="bin">Bin</option>
                <option value="warehouse">Warehouse</option>
                <option value="vehicle">Van</option>
              </select>
              <select
                className={`${field} w-44`}
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                aria-label="Inside"
              >
                <option value="">Not inside anything</option>
                {locations.data.map((l) => (
                  <option key={l.id} value={l.id}>
                    {'   '.repeat(l.depth)}
                    {l.name}
                  </option>
                ))}
              </select>
              <Button
                disabled={!name.trim() || create.isPending}
                onClick={() =>
                  create.mutate({
                    name: name.trim(),
                    kind,
                    parentId: parentId || null,
                    ...idem(),
                  })
                }
              >
                {create.isPending ? 'Adding…' : 'Add'}
              </Button>
              <Button variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => setAdding(true)}>
              <IconPlus size={14} />
              Add a location
            </Button>
          )}
        </>
      )}
    </SectionCard>
  );
}
